import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import Pagination from '../../../components/Pagination';
import { useAppAnnouncements } from '../../../hooks/useAppAnnouncement';
import Loading from '../../../components/Loading';
import { AppAnnouncement } from '../../../models/AppAnnouncement';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const COLUMNS = [
    { key: 'title', label: 'Title' },
    { key: 'files', label: 'Files' },
    { key: 'created', label: 'Created On' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppAnnouncementPage: React.FC = () => {
    const { loading, error, deleteAnnouncement, fetchAnnouncements, announcements } = useAppAnnouncements();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<number | null>(null);
    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');

    useEffect(() => {
        document.title = "App Announcements";
        fetchAnnouncements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStartDate, filterEndDate, filterYear]);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
    };

    const availableYears = Array.from(
        new Set(
            announcements
                .map((a) => new Date(a.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
    ];

    const filteredAnnouncements = announcements.filter((announcement) => {
        const matchesSearch = announcement.title?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStartDate = !filterStartDate || new Date(announcement.created_time) >= new Date(filterStartDate);
        const matchesEndDate = !filterEndDate || new Date(announcement.created_time) <= new Date(filterEndDate);
        const matchesYear = !filterYear || new Date(announcement.created_time).getFullYear().toString() === filterYear;
        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear;
    });
    const total = filteredAnnouncements.length;
    const pageAnnouncements = filteredAnnouncements.slice((currentPage - 1) * perPage, currentPage * perPage);

    const handleOpenDeleteDialog = (announcementId: number) => {
        setSelectedAnnouncementId(announcementId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedAnnouncementId) return;
        setIsDeleting(true);
        try {
            await deleteAnnouncement(selectedAnnouncementId);
            fetchAnnouncements();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete announcement:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Helper function to count files for an announcement
    const getFileCount = (announcement: AppAnnouncement): number => {
        let count = 0;
        if (announcement.image_url) count++;
        if (announcement.image_url_2) count++;
        if (announcement.image_url_3) count++;
        if (announcement.image_url_4) count++;
        if (announcement.image_url_5) count++;
        return count;
    };

    // Helper function to get file type breakdown
    const getFileTypeBreakdown = (announcement: AppAnnouncement): { images: number; pdfs: number } => {
        let images = 0;
        let pdfs = 0;

        [announcement.image_url, announcement.image_url_2, announcement.image_url_3, announcement.image_url_4, announcement.image_url_5].forEach(url => {
            if (url) {
                if (url.toLowerCase().endsWith('.pdf')) {
                    pdfs++;
                } else {
                    images++;
                }
            }
        });

        return { images, pdfs };
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>App Announcements</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => navigate('/gp/app/announcements/add')}><PlusIcon />Create new announcement</Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search announcement…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'title') && <TableHeader>Title</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'files') && <TableHeader>Files</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created On</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageAnnouncements.length > 0 ? [...pageAnnouncements].sort((a, b) => {
                                    const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((announcement, index) => {
                                    const fileCount = getFileCount(announcement);
                                    const fileTypes = getFileTypeBreakdown(announcement);

                                    return (
                                        <TableRow key={announcement.id}>
                                            <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                            {isColumnVisible(columnVisibility, 'title') && <TableCell>{announcement.title}</TableCell>}
                                            {isColumnVisible(columnVisibility, 'files') && <TableCell>
                                                {fileCount > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        <Badge color="zinc" className="text-xs">
                                                            {fileCount} file{fileCount !== 1 ? 's' : ''}
                                                        </Badge>
                                                        {fileTypes.images > 0 && (
                                                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                                                {fileTypes.images} image{fileTypes.images !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                        {fileTypes.pdfs > 0 && (
                                                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                                                                {fileTypes.pdfs} PDF{fileTypes.pdfs !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">No files</span>
                                                )}
                                            </TableCell>}
                                            {isColumnVisible(columnVisibility, 'created') && <TableCell>{dateFormat(announcement.created_time, "mmm dd, yyyy")}</TableCell>}
                                            <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                                <a onClick={() => navigate(`/gp/app/announcements/edit/${announcement.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                    <PencilIcon className='h-5 w-5' />
                                                </a>
                                                <a onClick={() => navigate(`/gp/app/announcements/view/${announcement.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                    <EyeIcon className='h-5 w-5' />
                                                </a>
                                                <Dropdown>
                                                    <DropdownButton plain aria-label="More options" className="h-5 w-5">
                                                        <EllipsisVerticalIcon />
                                                    </DropdownButton>
                                                    <DropdownMenu anchor="bottom end">
                                                        <DropdownItem onClick={() => handleOpenDeleteDialog(announcement.id)}>Delete</DropdownItem>
                                                    </DropdownMenu>
                                                </Dropdown>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8">No announcement found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Announcements'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
            </FiltersDialog>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Announcement</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this announcement? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>Cancel</Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AppAnnouncementPage;
